import {
  DefaultError,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { useInfiniteHandler, useQueryToast } from 'hooks';
import { useChunkUpload } from 'hooks/useChunkUpload';
import i18next from 'i18next';
import { apiClient } from 'utils';
import { getDefaultInfinitePageParam } from 'utils/service/defaultInfinitePageParam';
import { useResponseHandler } from 'utils/service/responseHandler';
import {
  IInconsistentStatementsFilterParams,
  IStatement,
  IStatementChunkedUploadPayload,
  IStatementChunkedUploadResponse,
  IStatementPartiallyOrFullyUnrelatedRow,
  IStatementPartiallyOrFullyUnrelatedRowsResponse,
  IStatementRow,
  IStatementRowProduct,
  IStatementRowsFilterParams,
  IStatementRowsResponse,
  IStatementStatistics,
  IStatementUnrelatedRowsResponse,
  IStatementUnrelatedRowsStatistics,
  IStatementsFilterParams,
  IStatementsResponse,
} from './types';

const cleanStatementParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params).filter(([_, v]) => {
      if (v === '' || v === undefined || v === null) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      return true;
    })
  );

const buildListParams = (
  base: IStatementsFilterParams,
  page: number,
  itemCount: number
) =>
  cleanStatementParams({
    magellanAccountId: base.magellanAccountId,
    startSettlementPeriodId: base.startSettlementPeriodId,
    endSettlementPeriodId: base.endSettlementPeriodId,
    salesChannels: base.salesChannels,
    providers: base.providers,
    hasUnrelatedRows: base.hasUnrelatedRows,
    showOtherStatuses: base.showOtherStatuses,
    page,
    itemCount,
  });

export const useGetStatementListInfinite = (params: IStatementsFilterParams) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    params.page,
    params.itemCount
  );

  return useInfiniteQuery<IStatement[], DefaultError>({
    queryKey: ['admin-statements', params],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement',
        params: buildListParams(params, pageParam as number, itemCount),
      });

      const value = await responseHandler<IStatementsResponse>(response, {
        groupName: `useGetStatementListInfinite - Page: ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam: getNextPageParam,
  });
};

export const useGetInconsistentStatements = (
  params: IInconsistentStatementsFilterParams
) => {
  const { responseHandler } = useResponseHandler();

  const cleanParams = cleanStatementParams({
    magellanAccountId: params.magellanAccountId,
    startSettlementPeriodId: params.startSettlementPeriodId,
    endSettlementPeriodId: params.endSettlementPeriodId,
    salesChannels: params.salesChannels,
  });

  return useQuery<IStatementsResponse, DefaultError>({
    queryKey: ['admin-inconsistent-statements', cleanParams],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/Inconsistent',
        params: cleanParams,
      });

      return responseHandler<IStatementsResponse>(response, {
        groupName: 'useGetInconsistentStatements',
      });
    },
  });
};

export const useGetStatementDetail = (
  statementId: string,
  magellanAccountId: string
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IStatement, DefaultError>({
    queryKey: ['admin-statement', statementId, magellanAccountId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statement/${statementId}`,
        params: {
          magellanAccountId,
          StatementId: statementId,
        },
      });

      return responseHandler(response, {
        groupName: 'useGetStatementDetail',
      });
    },
    enabled: !!statementId && !!magellanAccountId,
  });
};

const buildRowsParams = (
  magellanAccountId: string,
  statementId: string,
  page: number,
  itemCount: number,
  filters?: IStatementRowsFilterParams
) =>
  cleanStatementParams({
    magellanAccountId,
    statementId,
    page,
    itemCount,
    ean: filters?.ean,
    isrc: filters?.isrc,
    channelId: filters?.channelId,
    videoId: filters?.videoId,
    assetId: filters?.assetId,
  });

export const useGetStatementRowsInfinite = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    filters?.page,
    filters?.itemCount
  );

  return useInfiniteQuery<IStatementRow[], DefaultError>({
    queryKey: [
      'admin-statement-rows',
      statementId,
      magellanAccountId,
      filters,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/Rows',
        params: buildRowsParams(
          magellanAccountId,
          statementId,
          pageParam as number,
          itemCount,
          filters
        ),
      });

      const value = await responseHandler<IStatementRowsResponse>(response, {
        groupName: `useGetStatementRowsInfinite - ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam,
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const useGetStatementSummaryRowsInfinite = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    filters?.page,
    filters?.itemCount
  );

  return useInfiniteQuery<IStatementRow[], DefaultError>({
    queryKey: [
      'admin-statement-summary-rows',
      statementId,
      magellanAccountId,
      filters,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/SummaryRows',
        params: buildRowsParams(
          magellanAccountId,
          statementId,
          pageParam as number,
          itemCount,
          filters
        ),
      });

      const value = await responseHandler<IStatementRowsResponse>(response, {
        groupName: `useGetStatementSummaryRowsInfinite - ${pageParam}`,
      });

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam,
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const useGetStatementUnrelatedRowsInfinite = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    filters?.page,
    filters?.itemCount
  );

  return useInfiniteQuery<IStatementRow[], DefaultError>({
    queryKey: [
      'admin-statement-unrelated-rows',
      statementId,
      magellanAccountId,
      filters,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/UnrelatedRows',
        params: buildRowsParams(
          magellanAccountId,
          statementId,
          pageParam as number,
          itemCount,
          filters
        ),
      });

      const value = await responseHandler<IStatementUnrelatedRowsResponse>(
        response,
        {
          groupName: `useGetStatementUnrelatedRowsInfinite - ${pageParam}`,
        }
      );

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam,
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const useGetStatementUnrelatedRowsStatistics = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();

  const params = buildRowsParams(
    magellanAccountId,
    statementId,
    0,
    1,
    filters
  );

  return useQuery<IStatementUnrelatedRowsStatistics, DefaultError>({
    queryKey: [
      'admin-statement-unrelated-rows-statistics',
      statementId,
      magellanAccountId,
      filters,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/UnrelatedRows',
        params,
      });

      const value = await responseHandler<IStatementUnrelatedRowsResponse>(
        response,
        {
          groupName: 'useGetStatementUnrelatedRowsStatistics',
        }
      );

      return value.statistics;
    },
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const useGetStatementPartiallyOrFullyUnrelatedRowsInfinite = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();
  const { getNextPageParam, infiniteHandler } = useInfiniteHandler();

  const { itemCount, page } = getDefaultInfinitePageParam(
    filters?.page,
    filters?.itemCount
  );

  return useInfiniteQuery<IStatementPartiallyOrFullyUnrelatedRow[], DefaultError>({
    queryKey: [
      'admin-statement-partially-fully-unrelated-rows',
      statementId,
      magellanAccountId,
      filters,
    ],
    queryFn: async ({ pageParam }) => {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/PartiallyOrFullyUnrelatedRows',
        params: buildRowsParams(
          magellanAccountId,
          statementId,
          pageParam as number,
          itemCount,
          filters
        ),
      });

      const value =
        await responseHandler<IStatementPartiallyOrFullyUnrelatedRowsResponse>(
          response,
          {
            groupName: `useGetStatementPartiallyOrFullyUnrelatedRowsInfinite - ${pageParam}`,
          }
        );

      return infiniteHandler(value);
    },
    initialPageParam: page,
    getNextPageParam,
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const useGetStatementPartiallyOrFullyUnrelatedRowsStatistics = (
  statementId: string,
  magellanAccountId: string,
  filters?: IStatementRowsFilterParams
) => {
  const { responseHandler } = useResponseHandler();

  const params = buildRowsParams(
    magellanAccountId,
    statementId,
    0,
    1,
    filters
  );

  return useQuery<IStatementUnrelatedRowsStatistics, DefaultError>({
    queryKey: [
      'admin-statement-partially-fully-unrelated-rows-statistics',
      statementId,
      magellanAccountId,
      filters,
    ],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement/PartiallyOrFullyUnrelatedRows',
        params,
      });

      const value =
        await responseHandler<IStatementPartiallyOrFullyUnrelatedRowsResponse>(
          response,
          {
            groupName:
              'useGetStatementPartiallyOrFullyUnrelatedRowsStatistics',
          }
        );

      return value.statistics;
    },
    enabled: !!statementId && !!magellanAccountId,
  });
};

export const usePostStatementChunkedUpload = () => {
  const { responseHandler } = useResponseHandler();
  const { loading, success, dismiss } = useQueryToast(
    'usePostStatementChunkedUpload'
  );

  const { uploadChunkedFiles, progress, resetProgress } =
    useChunkUpload('Statement');

  const mutation = useMutation<
    IStatementChunkedUploadResponse,
    DefaultError,
    IStatementChunkedUploadPayload
  >({
    mutationFn: async ({
      magellanAccountId,
      salesChannelId,
      providerId,
      settlementPeriodId,
      templateType,
      file,
      onProgress,
    }) => {
      loading(
        i18next.t('service-message.upload.loading', {
          what: i18next.t('statements.popup.file'),
        })
      );

      const { totalChunks, importId } = await uploadChunkedFiles(
        file,
        onProgress
      );

      const finalResponse = await apiClient({
        method: 'POST',
        url: 'Statement/Import/Complete',
        body: {
          magellanAccountId,
          importId,
          totalChunks,
          salesChannelId,
          providerId,
          settlementPeriodId,
          templateType,
        },
      });

      return responseHandler(finalResponse, {
        groupName: 'usePostStatementChunkedUpload',
      });
    },
    onSuccess: () => {
      success(
        i18next.t('service-message.upload.success', {
          what: i18next.t('statements.popup.file'),
        })
      );
    },
    onError: dismiss,
  });

  return { ...mutation, progress, resetProgress };
};

export const useGetStatementRowProduct = (
  statementRowId: string,
  magellanAccountId: string,
  enabled = true
) => {
  const { responseHandler } = useResponseHandler();

  return useQuery<IStatementRowProduct, DefaultError>({
    queryKey: ['admin-statement-row-product', statementRowId, magellanAccountId],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: `Statement/Row/${statementRowId}/Product`,
        params: {
          magellanAccountId,
          statementRowId,
        },
      });

      return responseHandler(response, {
        groupName: 'useGetStatementRowProduct',
      });
    },
    enabled: enabled && !!statementRowId && !!magellanAccountId,
  });
};

export const useGetStatementStatistics = (params: IStatementsFilterParams) => {
  const { responseHandler } = useResponseHandler();

  const cleanParams = buildListParams(params, 0, 1);

  return useQuery<IStatementStatistics, DefaultError>({
    queryKey: ['admin-statement-statistics', cleanParams],
    async queryFn() {
      const response = await apiClient({
        method: 'GET',
        url: 'Statement',
        params: cleanParams,
      });

      const value = await responseHandler<IStatementsResponse>(response, {
        groupName: 'useGetStatementStatistics',
      });

      return value.statistics;
    },
  });
};
